<header class="head">
	<div class="head__text">
		<h2 class="lo-type-heading-3">Team</h2>
		@if (!seatFrozen) {
		<div class="seats">
			@if (loading) {
			<lo-skeleton variant="text"></lo-skeleton>
			} @else {
			<!-- The seat arithmetic. The bar is a picture of the count; the sentence
					     carries it, and the figure drawn beside it is hidden from assistive
					     technology so the line is not read twice. -->
			<div class="seats__content" data-testid="team-occupancy">
				<div class="seats__line">
					<span class="lo-type-text-md-semibold seats__label">
						Seats
					</span>
					<span class="lo-type-text-md seats__text">
						<span
							class="lo-visually-hidden"
							data-testid="team-seat-count"
						>
							{{ seatCountSentence }}
						</span>
						<span aria-hidden="true">{{ seatCountShort }}</span>
						{{ seatCountTail }}
					</span>
					<lo-icon
						class="seats__info"
						name="info_circle"
						label="About seats"
						data-testid="team-seat-note"
						tabindex="0"
						loTooltipTap
						[loTooltip]="seatTierNote"
					></lo-icon>
				</div>
			</div>
			}
		</div>
		}
	</div>
	@if (!seatFrozen) {
	<div class="head__action">
		@if (!upgradeToInvite) {
		<button
			lo-button
			type="button"
			data-testid="team-invite"
			[disabled]="!canInvite"
			(click)="openInvite($any($event.currentTarget))"
		>
			Invite a member
		</button>
		} @else {
		<!-- Every seat is taken and this reader may buy more: the way to invite
				     is a larger plan, so that is what the button offers. -->
		<button
			lo-button
			type="button"
			data-testid="team-see-plans"
			[loTooltip]="inviteBlockedReason"
			(click)="goToPlans()"
		>
			Upgrade to invite
		</button>
		}
		<!-- Beside Upgrade the seat line already says why, so the reason is the
			     button's tooltip and is only read out here; beside a greyed Invite
			     it is the one place the reason shows, so it is drawn. -->
		@if (inviteBlockedReason) {
		<p
			class="lo-type-text-sm head__action-note"
			data-testid="team-invite-blocked"
			[class.lo-visually-hidden]="upgradeToInvite"
		>
			{{ inviteBlockedReason }}
		</p>
		}
	</div>
	}
</header>

<!-- A failed read is not an account with nobody in it, so it gets its own state
     and never renders as an empty team. -->
@if (failed) {
<lo-empty-state
	bordered
	icon="lo_alert_triangle"
	heading="We could not read your team"
	body="Nobody has been removed — the request did not answer. Try again before assuming the account is empty."
	data-testid="team-unavailable"
>
	<button lo-button size="sm" type="button" (click)="retry()">
		Try again
	</button>
</lo-empty-state>
}

<!-- Every team route but the role matrix refuses a frozen seat, so this reader
     has no roster to be shown and no action to take. Its own state, because
     "we could not read your team" would blame the request for a decision. -->
@if (seatFrozen) {
<lo-notice
	tone="warning"
	heading="Your seat is frozen"
	body="Nothing has been deleted — your work, your mailboxes and your credit balance are all still on the account. The account owner can restore your seat as soon as one is free."
	data-testid="team-seat-frozen"
></lo-notice>
} @if (!seatFrozen && !failed) {
<lo-table
	caption="Team members"
	rowKey="id"
	emptyHeading="No members yet"
	emptyBody="Nobody has been seated on this account yet."
	[columns]="columns"
	[rows]="rows"
	[loading]="loading"
	[loadingRows]="5"
>
	<ng-template loTableCell="role" let-row>
		<span class="lo-table__truncate" [loTooltip]="row.roleTooltip">
			{{ row.roleLabel }}
		</span>
	</ng-template>

	<ng-template loTableCell="seat" let-row>
		<lo-status [tone]="row.seatTone" data-testid="team-seat-state">
			{{ row.seatLabel }}
		</lo-status>
	</ng-template>

	<!-- The member's request sits after their own figure — it is a fact about
		     that balance. Wraps under it when the figure is too long to share the
		     line. -->
	<ng-template loTableCell="credits" let-row>
		<div class="cell-credits">
			<span data-testid="team-member-credits">{{ row.credits }}</span>
			@if (row.request) {
			<lo-status
				tone="warning"
				data-testid="team-credit-request-mark"
				[loTooltip]="row.requestMarkTooltip"
			>
				{{ row.requestMark }}
			</lo-status>
			} @if (row.creditsPending) {
			<lo-status
				tone="info"
				dot
				role="status"
				data-testid="team-member-credits-pending"
			>
				Adding credits…
			</lo-status>
			}
		</div>
	</ng-template>

	<!-- At most one visible button: Top up, where a request is open.
		     Everything else is in the row menu. -->
	<ng-template loTableCell="actions" let-row>
		<div class="cell-actions" #cellActions>
			@if (row.request && row.topUpOffered) {
			<button
				lo-button
				variant="secondary"
				size="xs"
				type="button"
				aria-haspopup="dialog"
				data-testid="team-credit-request-open"
				(click)="openTopUp(row, $any($event.currentTarget))"
			>
				Top up
			</button>
			} @if (row.menuItems.length) {
			<button
				lo-button
				variant="ghost"
				size="sm"
				iconOnly
				round
				type="button"
				data-testid="team-row-menu"
				[attr.aria-label]="row.actionsLabel"
				[loMenuTrigger]="rowMenu"
			>
				<lo-icon name="dots"></lo-icon>
			</button>
			<lo-menu #rowMenu [label]="row.actionsLabel">
				@for (item of row.menuItems; track item.value) { @if
				(item.separatorBefore) {
				<lo-menu-divider></lo-menu-divider>
				}
				<button
					lo-menu-item
					type="button"
					[destructive]="item.variant === 'destructive'"
					[disabled]="item.disabled"
					[loTooltip]="item.tooltip ?? ''"
					(click)="onRowAction(item.value, row, cellActions)"
				>
					{{ item.title }}
				</button>
				}
			</lo-menu>
			}
		</div>
	</ng-template>
</lo-table>
}

<!-- An invitation holds a seat before anybody sits in it, so it belongs on the
     page that states the seat arithmetic — otherwise an owner who has just
     invited somebody sees a count move with nothing to explain it. -->
@if (!seatFrozen && (invitesLoading || invites.length)) {
<lo-card
	heading="Waiting to accept"
	info="Their seat is held from the moment the invitation goes out."
	data-testid="team-invites"
>
	<lo-table
		compact
		caption="Invitations waiting to accept"
		[columns]="inviteColumns"
		[rows]="invites"
		[loading]="invitesLoading"
		[loadingRows]="2"
	>
		<ng-template loTableCell="actions" let-row>
			<div class="cell-actions">
				<button
					lo-button
					variant="secondary"
					size="xs"
					type="button"
					data-testid="team-invite-revoke"
					[disabled]="!canRevokeInvite || row.busy"
					(click)="revokeInvite(row)"
				>
					Withdraw
				</button>
			</div>
		</ng-template>
	</lo-table>
</lo-card>
}

<!-- The top-up dialog's body, one definition for both of its openers. -->
<ng-template #topUpDialog>
	@if (topUpRow; as row) {
	<lo-dialog
		heading="Top up {{ row.name }}"
		data-testid="team-credit-request"
	>
		<div class="top-up">
			@if (packOptions.length) {
			<lo-radio-group
				size="md"
				data-testid="team-credit-request-packs"
				[ariaLabel]="'Credit pack for ' + row.name"
				[options]="packOptions"
				[value]="row.topUpPackId"
				[disabled]="row.topUpBusy"
				(valueChange)="onTopUpPackChange($event)"
			></lo-radio-group>
			} @if (packsLoaded && !packOptions.length) {
			<p class="lo-type-text-sm top-up__note">
				We could not read the credit packs on offer. Reload the page to
				try again.
			</p>
			} @if (row.topUpDecline) {
			<p
				class="lo-type-text-sm top-up__decline"
				role="alert"
				data-testid="team-credit-request-declined"
			>
				{{ row.topUpDecline }}
			</p>
			}
		</div>
		<div loDialogActions>
			<!-- Only a request can be dismissed; a top-up nobody asked for is simply
				     closed. -->
			@if (row.request) {
			<button
				lo-button
				variant="secondary"
				type="button"
				data-testid="team-credit-request-dismiss"
				[disabled]="row.topUpBusy"
				(click)="dismissRequest(row)"
			>
				Not now — dismiss
			</button>
			}
			<button
				lo-button
				type="button"
				data-testid="team-credit-request-top-up"
				[loading]="row.topUpBusy"
				[disabled]="!row.topUpPackId"
				(click)="topUp(row)"
			>
				{{ row.topUpLabel }}
			</button>
		</div>
	</lo-dialog>
	}
</ng-template>
